// Referrals (docs/plans/phase2-referrals.md §3.3). Internal IDs, statuses, dates and a
// facility-name fragment in addresses; reasons, notes, summaries and letters in bodies.
// Searches put their words in the body.
import { queryOptions } from '@tanstack/react-query';
import {
  errorBodySchema,
  patientReferralListSchema,
  referralDetailSchema,
  referralFacilityListSchema,
  referralFeedbackLetterSchema,
  referralFeedbackPrintSchema,
  referralListSchema,
  referralPreviewSchema,
  type IssueFeedbackLetterRequest,
  type LogIncomingReferralRequest,
  type RecordReferralUpdateRequest,
  type ReferralRegistrySearchRequest,
} from '@wodi/shared';
import { z } from 'zod';

import { ApiError, api, errorMessage, OFFLINE_MESSAGE } from './client.ts';

export interface ReferralListFilters {
  direction: 'outgoing' | 'incoming';
  status?: string | undefined;
  urgency?: string | undefined;
  facility?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
}

export const referralKeys = {
  all: ['referrals'] as const,
  list: (filters: ReferralListFilters) => ['referrals', 'list', filters] as const,
  detail: (id: string) => ['referrals', 'detail', id] as const,
  patient: (patientId: string) => ['patients', patientId, 'referral-records'] as const,
  feedbackPreview: (id: string) => ['referrals', 'detail', id, 'feedback-preview'] as const,
  feedbackPrint: (id: string) => ['referral-feedback-letters', id, 'print'] as const,
};

/** The filters as query parameters, leaving out the empty ones. */
export function filterParams(filters: ReferralListFilters): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (typeof value === 'string' && value.trim() !== '') params.set(key, value.trim());
  }
  return params;
}

export function fetchReferralPage(filters: ReferralListFilters, cursor: string | null) {
  const params = filterParams(filters);
  params.set('limit', '50');
  if (cursor !== null) params.set('cursor', cursor);
  return api('GET', `/referral-records?${params.toString()}`, { schema: referralListSchema });
}

export const referralQuery = (id: string) =>
  queryOptions({
    queryKey: referralKeys.detail(id),
    queryFn: () => api('GET', `/referral-records/${id}`, { schema: referralDetailSchema }),
  });

export const patientReferralsQuery = (patientId: string) =>
  queryOptions({
    queryKey: referralKeys.patient(patientId),
    queryFn: () =>
      api('GET', `/patients/${patientId}/referral-records`, { schema: patientReferralListSchema }),
  });

export function searchReferralFacilities(q: string) {
  return api('POST', '/referral-facilities/search', {
    body: { q },
    schema: referralFacilityListSchema,
  });
}

export function searchRegistryFacilities(body: ReferralRegistrySearchRequest) {
  return api('POST', '/referral-facilities/registry-search', {
    body,
    schema: referralFacilityListSchema,
  });
}

export function logIncomingReferral(visitId: string, body: LogIncomingReferralRequest) {
  return api('POST', `/visits/${visitId}/incoming-referrals`, {
    body,
    schema: referralDetailSchema,
  });
}

export function recordReferralUpdate(id: string, body: RecordReferralUpdateRequest) {
  return api('POST', `/referral-records/${id}/updates`, { body, schema: referralDetailSchema });
}

export function referralEnteredInError(id: string, reason: string) {
  return api('POST', `/referral-records/${id}/entered-in-error`, {
    body: { reason },
    schema: referralDetailSchema,
  });
}

export const feedbackPreviewQuery = (id: string) =>
  queryOptions({
    queryKey: referralKeys.feedbackPreview(id),
    queryFn: () =>
      api('POST', `/referral-records/${id}/feedback-preview`, { schema: referralPreviewSchema }),
    staleTime: 0,
  });

export function issueFeedbackLetter(id: string, body: IssueFeedbackLetterRequest) {
  return api('POST', `/referral-records/${id}/feedback-letters`, {
    body,
    schema: referralFeedbackLetterSchema,
  });
}

export const feedbackPrintQuery = (id: string) =>
  queryOptions({
    queryKey: referralKeys.feedbackPrint(id),
    queryFn: () =>
      api('GET', `/referral-feedback-letters/${id}/print`, {
        schema: referralFeedbackPrintSchema,
      }),
  });

export function markFeedbackPrinted(id: string) {
  return api('POST', `/referral-feedback-letters/${id}/printed`, {
    schema: z.object({ copy: z.number() }),
  });
}

export function feedbackEnteredInError(id: string, reason: string) {
  return api('POST', `/referral-feedback-letters/${id}/entered-in-error`, {
    body: { reason },
    schema: referralFeedbackLetterSchema,
  });
}

const FILE_NAME = /filename="([^"]+)"/;

/**
 * Downloads the list as CSV with the same filters. The server audits the export and names
 * the file from the direction and the day only.
 */
export async function downloadReferrals(filters: ReferralListFilters): Promise<string> {
  let response: Response;
  try {
    response = await fetch(`/api/v1/referral-records/export?${filterParams(filters).toString()}`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { accept: 'text/csv' },
    });
  } catch {
    throw new ApiError(0, 'offline', OFFLINE_MESSAGE);
  }
  if (!response.ok) {
    const parsed = errorBodySchema.safeParse(await response.json().catch(() => null));
    if (parsed.success) {
      const { code, message, requestId } = parsed.data.error;
      throw new ApiError(response.status, code, errorMessage(message, requestId), requestId);
    }
    throw new ApiError(response.status, 'unexpected_response', 'Something went wrong. Try again.');
  }
  const name =
    FILE_NAME.exec(response.headers.get('content-disposition') ?? '')?.[1] ?? 'wodi_referrals.csv';
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.append(link);
    link.click();
    link.remove();
  } finally {
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1_000);
  }
  return name;
}
